import {
  Button,
  Container,
  ContentLayout,
  Grid,
  Header,
} from '@cloudscape-design/components';
import { OrderStatus } from '@parallel-fluidics/shared/constants';
import { uploadPartFromFirestore } from '@parallel-fluidics/shared/models/parts';
import PartsList from 'components/PartsList';
import useSortParts from 'components/PartsList/useSortParts';
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActiveOrdersSummary } from '../../components/ActiveOrders';
import {
  OrderCalendar,
  OrderList,
} from '../../components/Orders';
import OrderDetailProvider from '../../components/Orders/orderDetailProvider';
import useRealtimeDocs from '../../hooks/use-realtimeDocs';

function Home() {
  const { partsSort, setPartsSort } = useSortParts();
  const recentPartsQuery = useMemo(() => query(
    collection(getFirestore(), 'parts'),
    orderBy(partsSort.field, partsSort.isDescending ? 'desc' : 'asc'),
    limit(10),
  ), [partsSort]);
  const recentOrdersQuery = useMemo(() => query(
    collection(getFirestore(), 'orders'),
    orderBy('orderDate', 'desc'),
    limit(30),
  ), []);
  const activeOrdersQuery = useMemo(() => query(
    collection(getFirestore(), 'orders'),
    where('status', '==', OrderStatus.IN_PROGRESS),
  ), []);
  const { data: parts, status: partsStatus } = useRealtimeDocs({
    query: recentPartsQuery,
    transformData: uploadPartFromFirestore,
  });
  const { data: recentOrders, status: ordersStatus } = useRealtimeDocs({
    query: recentOrdersQuery,
  });
  const { data: activeOrders, status: activeOrdersStatus } = useRealtimeDocs({
    query: activeOrdersQuery,
  });

  const orders = useMemo(() => {
    if (ordersStatus === 'loading' || activeOrdersStatus === 'loading') {
      return [];
    }
    // merge + dedupe by id
    const byId = new Map();
    recentOrders.forEach((order) => {
      byId.set(order.id, order);
    });
    activeOrders.forEach((order) => {
      byId.set(order.id, order);
    });

    const merged = Array.from(byId.values());

    // sort by orderDate desc ("YYYY-MM-DD" compares correctly as strings)
    merged.sort((a, b) => {
      const aDate = a.orderDate || '';
      const bDate = b.orderDate || '';
      return bDate.localeCompare(aDate); // newest first
    });
    return merged;
  }, [ordersStatus, activeOrdersStatus, activeOrders, recentOrders]);

  const [hasActiveOrders, setHasActiveOrders] = useState(true);

  useEffect(() => {
    const checkOrders = async () => {
      try {
        // Cheapest option: limit(1) + empty check
        const q = query(
          collection(getFirestore(), 'orders'),
          where('status', '==', OrderStatus.IN_PROGRESS),
          limit(1),
        );

        const snapshot = await getDocs(q);
        setHasActiveOrders(!snapshot.empty);
      } catch (error) {
        setHasActiveOrders(false);
      }
    };

    checkOrders();
  }, []);

  const navigate = useNavigate();

  return (
    <OrderDetailProvider>
      <ContentLayout>
        <Grid gridDefinition={[
          ...(hasActiveOrders
            ? [{ colspan: { s: 8, default: 12 } }, { colspan: { s: 4, default: 12 } }]
            : [{ colspan: 12 }]
          ),
          ...[{ colspan: { s: 6, default: 12 } }, { colspan: { s: 6, default: 12 } }],
        ]}
        >
          <Container
            fitHeight
            header={<Header>Upcoming ship dates</Header>}
          >
            <OrderCalendar
              orders={orders}
              isLoading={ordersStatus === 'loading' || activeOrdersStatus === 'loading'}
            />
          </Container>
          {hasActiveOrders ? (
            <ActiveOrdersSummary />
          ) : null}
          <Container
            fitHeight
            header={(
              <Header actions={<Button variant="normal" onClick={() => { navigate('/orders'); }}>See all orders</Button>}>
                Orders
              </Header>
            )}
          >
            <OrderList
              variant="embedded"
              orders={recentOrders.slice(0, 10)}
              isLoading={ordersStatus === 'loading'}
            />
          </Container>
          <Container
            fitHeight
            header={(
              <Header actions={<Button variant="normal" onClick={() => { navigate('/parts'); }}>See all uploads</Button>}>
                Recent uploads
              </Header>
            )}
          >
            <PartsList
              variant="embedded"
              parts={parts}
              isLoading={partsStatus === 'loading'}
              sorting={partsSort}
              onSortingChange={setPartsSort}
            />
          </Container>
        </Grid>
      </ContentLayout>
    </OrderDetailProvider>
  );
}

export default Home;
